import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { LeaderSearchInput } from "../../../../components/LeaderSearchInput";
import type { WhitelistEntry } from "../../../../types/whitelist";

export interface EditModalProps {
  entry: WhitelistEntry | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (email: string, isPj: boolean | null, isTech: boolean | null, responsibleManager: string | null) => Promise<void>;
  isSaving: boolean;
}

export const EditModal: React.FC<EditModalProps> = ({ entry, isOpen, onClose, onSave, isSaving }) => {
  const [isPj, setIsPj]                             = useState<boolean>(false);
  const [isTech, setIsTech]                         = useState<boolean>(false);
  const [responsibleManager, setResponsibleManager] = useState<string>("");

  const labelColor  = useColorModeValue("gray.500", "gray.400");
  const inputBg     = useColorModeValue("gray.50", "#0a1628");
  const bg          = useColorModeValue("white", "#0f1f3a");
  const entryCardBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const entryCardBorder = useColorModeValue("gray.100", "whiteAlpha.100");

  useEffect(() => {
    if (entry) {
      setIsPj(entry.is_pj ?? false);
      setIsTech(entry.is_tech ?? false);
      setResponsibleManager(entry.responsible_manager ?? "");
    }
  }, [entry]);

  const handleSave = () => {
    if (!entry) return;
    onSave(entry.email, isPj, isTech, responsibleManager.trim() || null);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
      <ModalContent borderRadius="2xl" bg={bg}>
        <ModalHeader fontSize="md" fontWeight={600} pb={1}>Editar classificação</ModalHeader>
        <ModalCloseButton />
        <ModalBody pt={2} pb={4}>
          {entry && (
            <VStack align="stretch" spacing={4}>
              <Box
                p={3}
                borderRadius="lg"
                bg={entryCardBg}
                borderWidth="1px"
                borderColor={entryCardBorder}
              >
                <Text fontSize="sm" fontWeight={600} noOfLines={1}>
                  {entry.candidate_full_name ?? entry.email}
                </Text>
                {entry.candidate_full_name && (
                  <Text fontSize="xs" color={labelColor} noOfLines={1}>{entry.email}</Text>
                )}
              </Box>

              <FormControl>
                <FormLabel mb={1} fontSize="sm" fontWeight={500}>Gestor responsável</FormLabel>
                <LeaderSearchInput
                  value={responsibleManager}
                  onChange={setResponsibleManager}
                  placeholder="Buscar gestor..."
                  size="sm"
                  bg={inputBg}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={0} fontSize="sm" fontWeight={500}>Contratação PJ</FormLabel>
                  <Text fontSize="xs" color={labelColor}>Candidato será contratado como PJ</Text>
                </Box>
                <Switch isChecked={isPj} onChange={(e) => setIsPj(e.target.checked)} colorScheme="orange" size="md" />
              </FormControl>

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={0} fontSize="sm" fontWeight={500}>Vaga de Tecnologia</FormLabel>
                  <Text fontSize="xs" color={labelColor}>Auto-detectado pelo Gupy, editável manualmente</Text>
                </Box>
                <Switch isChecked={isTech} onChange={(e) => setIsTech(e.target.checked)} colorScheme="blue" size="md" />
              </FormControl>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2} pt={2}>
          <Button variant="ghost" size="sm" onClick={onClose} isDisabled={isSaving}>Cancelar</Button>
          <Button colorScheme="blue" size="sm" onClick={handleSave} isLoading={isSaving} borderRadius="lg">Salvar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
